/**
 * CSV parsing utilities
 */

/**
 * Parse CSV text into array of objects
 * Handles quoted fields and escaped quotes
 */
export function parseCSV<T extends Record<string, unknown>>(csvText: string, options: { skipFirstLine?: boolean } = {}): T[] {
  const lines = csvText.trim().split(/\r?\n/);
  let startIndex = 0;

  // Skip "sep=," line if present
  if (lines[0]?.startsWith('"sep=') || lines[0]?.startsWith('sep=')) {
    startIndex = 1;
  }

  // Additional skip if requested
  if (options.skipFirstLine) {
    startIndex += 1;
  }

  if (lines.length <= startIndex) {
    return [];
  }

  const headers = parseCSVLine(lines[startIndex]);
  const results: T[] = [];

  for (let i = startIndex + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = parseCSVLine(line);
    const obj: Record<string, string> = {};

    headers.forEach((header, index) => {
      obj[header] = values[index] ?? '';
    });

    results.push(obj as T);
  }

  return results;
}

/**
 * Parse a single CSV line, handling quoted fields
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        // Check for escaped quote
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i += 2;
          continue;
        }
        // End of quoted field
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    i++;
  }

  result.push(current.trim());
  return result;
}

/**
 * Generate CSV from array of objects
 */
export function generateCSV<T extends Record<string, unknown>>(data: T[], columns: { key: keyof T; header: string }[]): string {
  const headers = columns.map((c) => escapeCSVField(c.header));
  const rows = data.map((row) => columns.map((c) => escapeCSVField(String(row[c.key] ?? ''))).join(','));

  return [headers.join(','), ...rows].join('\n');
}

/**
 * Escape a field for CSV output
 */
function escapeCSVField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
